// Typed client for the AI trading-assistant config endpoints:
//   /api/AiSettings        — per-user settings (BYO key vs system AI, mode)
//   /api/admin/ai-settings — SuperAdmin: the system-provided AI
// API keys are masked on read; send a blank key to keep the stored one.

import { API_BASE } from '@/env';
import { apiFetch } from '@/auth/apiClient';

export type AiProvider = 'OpenAi' | 'Anthropic';
export type TradeAuthorityMode = 'ProposeOnly' | 'AutonomousPaper' | 'FullyAutonomous';

/** System-AI availability surfaced to the per-user settings UI (no key). */
export interface SystemAiInfo {
  available: boolean;
  reason: string | null;
  provider: string;
  model: string | null;
}

/** The caller's own AI settings (GET/PUT /api/AiSettings). */
/** One tool the assistant can use, with the user's enable/disable state. */
export interface AiToolInfo {
  name: string;
  description: string;
  enabled: boolean;
}

export interface UserAiSettings {
  useSystemAi: boolean;
  provider: AiProvider;
  apiKeyMasked: string | null;
  hasOwnKey: boolean;
  model: string | null;
  baseUrl: string | null;
  tradeAuthorityMode: TradeAuthorityMode;
  disabledTools: string[];
  tools: AiToolInfo[];
  updatedAt: string | null;
  systemAi: SystemAiInfo;
}

export interface UpdateUserAiSettings {
  useSystemAi: boolean;
  provider: AiProvider;
  apiKey?: string;       // blank keeps the stored key
  model?: string;
  baseUrl?: string;
  tradeAuthorityMode: TradeAuthorityMode;
  disabledTools?: string[];
}

/** The system-provided AI (GET/PUT /api/admin/ai-settings, SuperAdmin). */
export interface AdminAiSettings {
  enabled: boolean;
  provider: AiProvider;
  apiKeyMasked: string | null;
  hasApiKey: boolean;
  model: string | null;
  baseUrl: string | null;
  minimumTier: string;
  updatedAt: string;
  updatedByUserId: string | null;
}

export interface UpdateAdminAiSettings {
  enabled: boolean;
  provider: AiProvider;
  apiKey?: string;       // blank keeps the stored key
  model?: string;
  baseUrl?: string;
  minimumTier: string;
}

async function getJson<T>(path: string): Promise<T> {
  const res = await apiFetch(`${API_BASE}${path}`);
  if (!res.ok) throw new Error(await res.text() || `Request failed (${res.status})`);
  return res.json() as Promise<T>;
}

async function putJson<T>(path: string, body: unknown): Promise<T> {
  const res = await apiFetch(`${API_BASE}${path}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!res.ok) throw new Error(await res.text() || `Request failed (${res.status})`);
  return res.json() as Promise<T>;
}

export const aiSettingsService = {
  /** The caller's AI settings + whether system AI is available to them. */
  get: () => getJson<UserAiSettings>('/api/AiSettings'),
  update: (req: UpdateUserAiSettings) => putJson<UserAiSettings>('/api/AiSettings', req),

  /** The system-provided AI config. SuperAdmin only — 403 otherwise. */
  getSystem: () => getJson<AdminAiSettings>('/api/admin/ai-settings'),
  updateSystem: (req: UpdateAdminAiSettings) =>
    putJson<AdminAiSettings>('/api/admin/ai-settings', req),
};
